import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { callBackend } from '../utils/api';
import { frostedPanelBg } from '../styles/surfaceSx';
import {
    secondaryActionButtonSx,
    secondaryPanelSx,
} from '../styles/secondaryMsiStyles';

const CONNECTION_STATE_LABEL = {
    disconnected: 'Disconnected',
    pending: 'Pending',
    connected: 'Connected',
    error: 'Error',
};

const EXECUTION_FEED_STATE_LABEL = {
    not_configured: 'Not Configured',
    pending: 'Pending',
    configured: 'Configured',
    error: 'Error',
};

function gateSeverityFromState(gateState) {
    if (gateState === 'active') return 'success';
    if (gateState === 'setup_complete_but_empty') return 'info';
    return 'warning';
}

function chipColorFromState(state) {
    if (state === 'connected' || state === 'configured') return 'success';
    if (state === 'error') return 'error';
    if (state === 'pending') return 'warning';
    return 'default';
}

export default function ExecutionDataSourcePanel({
    selectedProviderId = null,
    onSelectionChange,
    refreshToken = 0,
    hideHeader = false,
    liveCheckOnLoad = true,
    autoSelectOnLoad = true,
}) {
    const [statusPayload, setStatusPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const autoSelectedRef = useRef(false);

    const fetchStatus = useCallback(async ({ liveCheck = liveCheckOnLoad } = {}) => {
        setLoading(true);
        setError('');
        try {
            const endpoint = liveCheck
                ? 'api/execution/providers/status?live_check=true'
                : 'api/execution/providers/status';
            const response = await callBackend(endpoint, 'GET');
            setStatusPayload(response || null);
            return response || null;
        } catch (statusError) {
            setError(statusError?.message || 'Failed to load execution provider status.');
            return null;
        } finally {
            setLoading(false);
        }
    }, [liveCheckOnLoad]);

    useEffect(() => {
        void fetchStatus({ liveCheck: liveCheckOnLoad });
    }, [fetchStatus, liveCheckOnLoad, refreshToken]);

    const selectProvider = useCallback(async (providerId, { silent = false } = {}) => {
        if (!providerId) return;
        setSaving(true);
        if (!silent) {
            setError('');
        }
        try {
            const response = await callBackend('api/execution/providers/select', 'POST', {
                provider_id: providerId,
            });
            setStatusPayload(response || null);
            onSelectionChange?.(response?.selected_provider_id || null);
        } catch (selectionError) {
            setError(selectionError?.message || 'Failed to select execution provider.');
        } finally {
            setSaving(false);
        }
    }, [onSelectionChange]);

    useEffect(() => {
        if (!statusPayload || loading || saving) return;
        if (!autoSelectOnLoad) return;
        if (statusPayload.selected_provider_id) return;
        if (!statusPayload.default_provider_id) return;
        if (autoSelectedRef.current) return;
        autoSelectedRef.current = true;
        void selectProvider(statusPayload.default_provider_id, { silent: true });
    }, [autoSelectOnLoad, loading, saving, selectProvider, statusPayload]);

    const providers = useMemo(
        () => (Array.isArray(statusPayload?.providers) ? statusPayload.providers : []),
        [statusPayload?.providers],
    );
    const activeProvider = useMemo(
        () => providers.find((item) => item?.provider_id === statusPayload?.active_provider_id) || null,
        [providers, statusPayload?.active_provider_id],
    );
    const effectiveSelectedProviderId = statusPayload?.selected_provider_id || selectedProviderId || null;
    const gateState = String(statusPayload?.gate_state || 'inactive_setup_required');
    const gateTitle = String(statusPayload?.gate_title || 'Execution Data Source Required');
    const gateMessage = String(
        statusPayload?.gate_message
        || 'Connect and configure an execution provider to enable Performance data.'
    );
    const gateActions = Array.isArray(statusPayload?.gate_actions)
        ? statusPayload.gate_actions.filter(Boolean)
        : [];
    const gateErrorDetail = String(statusPayload?.gate_error_detail || '').trim();

    return (
        <Paper
            variant="outlined"
            sx={{
                ...secondaryPanelSx,
                p: 1.25,
                borderRadius: 2,
                backgroundColor: (theme) => frostedPanelBg(theme, { darkAlpha: 0.24, lightAlpha: 0.56 }),
                display: 'grid',
                gap: 1,
            }}
        >
            {!hideHeader && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Execution Data Source
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Select one provider for Performance execution data.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { void fetchStatus({ liveCheck: true }); }}
                        disabled={loading || saving}
                        sx={{ textTransform: 'none', fontWeight: 700, ...secondaryActionButtonSx }}
                    >
                        {loading ? <CircularProgress size={14} /> : 'Refresh'}
                    </Button>
                </Box>
            )}

            <Alert
                severity={gateSeverityFromState(gateState)}
                variant="outlined"
                sx={{ py: 0.2 }}
            >
                <Box sx={{ display: 'grid', gap: 0.55 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {gateTitle}
                    </Typography>
                    <Typography variant="body2">
                        {gateMessage}
                    </Typography>
                    {gateErrorDetail ? (
                        <Typography variant="caption" color="text.secondary">
                            Detail: {gateErrorDetail}
                        </Typography>
                    ) : null}
                    {gateActions.length > 0 ? (
                        <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.25 }}>
                            {gateActions.map((action) => (
                                <li key={action}>
                                    <Typography variant="caption">{action}</Typography>
                                </li>
                            ))}
                        </Box>
                    ) : null}
                </Box>
            </Alert>

            {error && (
                <Alert severity="error" variant="outlined">
                    {error}
                </Alert>
            )}

            {loading && !statusPayload ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading provider status...</Typography>
                </Box>
            ) : null}

            {!loading && providers.length === 0 ? (
                <Alert severity="info" variant="outlined">
                    No execution providers are available yet.
                </Alert>
            ) : null}

            {providers.map((provider) => {
                const providerId = String(provider?.provider_id || '');
                const isSelected = providerId && providerId === effectiveSelectedProviderId;
                const isSelectable = Boolean(provider?.is_selectable);
                const connectionState = String(provider?.connection_state || 'disconnected');
                const feedState = String(provider?.execution_feed_state || 'not_configured');
                const historicalReady = Boolean(provider?.has_historical_data);

                return (
                    <Box
                        key={providerId}
                        sx={{
                            border: '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 1.6,
                            p: 1,
                            opacity: isSelectable ? 1 : 0.72,
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {provider?.display_name || providerId}
                            </Typography>
                            {isSelected ? <Chip size="small" color="primary" label="Selected" /> : null}
                            {!isSelectable ? <Chip size="small" variant="outlined" label="Unavailable" /> : null}
                        </Stack>

                        <Stack direction="row" spacing={0.7} sx={{ mb: 0.9, flexWrap: 'wrap' }}>
                            <Chip
                                size="small"
                                color={chipColorFromState(connectionState)}
                                variant="outlined"
                                label={`Connection: ${CONNECTION_STATE_LABEL[connectionState] || connectionState}`}
                            />
                            <Chip
                                size="small"
                                color={chipColorFromState(feedState)}
                                variant="outlined"
                                label={`Execution Feed: ${EXECUTION_FEED_STATE_LABEL[feedState] || feedState}`}
                            />
                            <Chip
                                size="small"
                                color={historicalReady ? 'success' : 'default'}
                                variant="outlined"
                                label={historicalReady ? 'History: Ready' : 'History: Not Synced'}
                            />
                        </Stack>

                        {provider?.last_error ? (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.8 }}>
                                Last error: {provider.last_error}
                            </Typography>
                        ) : null}
                        {provider?.status_message ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                {provider.status_message}
                            </Typography>
                        ) : null}
                        {Array.isArray(provider?.recommended_actions) && provider.recommended_actions.length > 0 ? (
                            <Box component="ul" sx={{ mt: 0, mb: 0.9, pl: 2.25 }}>
                                {provider.recommended_actions.map((action) => (
                                    <li key={action}>
                                        <Typography variant="caption">{action}</Typography>
                                    </li>
                                ))}
                            </Box>
                        ) : null}

                        <Button
                            variant={isSelected ? 'contained' : 'outlined'}
                            size="small"
                            disabled={!isSelectable || isSelected || saving}
                            onClick={() => { void selectProvider(providerId); }}
                            sx={{ textTransform: 'none', fontWeight: 700, ...secondaryActionButtonSx }}
                        >
                            {isSelected ? 'Selected' : 'Use For Performance'}
                        </Button>
                    </Box>
                );
            })}

            {providers.length > 0 ? <Divider /> : null}

            <Typography variant="caption" color="text.secondary">
                Active source: {activeProvider?.display_name || 'None selected'}.
            </Typography>
        </Paper>
    );
}

ExecutionDataSourcePanel.propTypes = {
    autoSelectOnLoad: PropTypes.bool,
    hideHeader: PropTypes.bool,
    liveCheckOnLoad: PropTypes.bool,
    selectedProviderId: PropTypes.string,
    onSelectionChange: PropTypes.func,
    refreshToken: PropTypes.number,
};
